import { useState } from 'react';
import { FunctionEntry } from '@/types/simulation';
import { useTheme } from '@/contexts/ThemeContext';

interface Props {
  functions: FunctionEntry[];
  onAdd: () => void;
  onUpdate: (id: string, updates: Partial<FunctionEntry>) => void;
  onRemove: (id: string) => void;
  onResetAll?: () => void;
  onOpenImport?: () => void;
}

const PRESET_COLORS = [
  '#00d9ff', '#ff6b35', '#ffd700', '#ff4da6', '#7fff00', '#c084fc', '#ff8c00',
];

const EXAMPLE_EXPRESSIONS = [
  'sin(x)', 'cos(x)', 'x^2', 'x^3 - 3*x', 'sqrt(abs(x))',
  '1/x', 'exp(-x^2)', 'tan(x)', '2*x + 1', 'sin(x)*cos(x)',
];

export default function FunctionInputSection({ functions, onAdd, onUpdate, onRemove, onResetAll, onOpenImport }: Props) {
  const [showExamples, setShowExamples] = useState(false);
  const [activeExampleFor, setActiveExampleFor] = useState<string | null>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const textMuted     = isDark ? 'text-white/35' : 'text-gray-500';
  const textSecondary = isDark ? 'text-white/60' : 'text-gray-600';
  const textPrimary   = isDark ? 'text-white'    : 'text-gray-900';
  const textDim       = isDark ? 'text-white/20' : 'text-gray-300';
  const placeholder   = isDark ? 'placeholder-white/20' : 'placeholder-gray-300';

  const handleExampleClick = (expr: string) => {
    const target = activeExampleFor || functions[functions.length - 1]?.id;
    if (target) onUpdate(target, { expression: expr });
    setShowExamples(false);
  };

  return (
    <div className="space-y-3 animate-float-in">
      <div className="flex items-center justify-between">
        <h2 className="section-label flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-function-line ${isDark ? 'text-cyan-400' : 'text-sky-600'}`} />
          </div>
          関数入力
        </h2>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onResetAll?.()}
            className={`glass-btn ripple-btn text-xs ${textDim} hover:text-red-400 px-2 py-1 rounded-full cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors`}
            title="全関数をリセット"
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-refresh-line text-xs" />
            </div>
            リセット
          </button>
          <button
            onClick={() => onOpenImport?.()}
            className={`glass-btn ripple-btn text-xs ${isDark ? 'text-cyan-400/70' : 'text-sky-500'} hover:${textPrimary} px-2 py-1 rounded-full cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors`}
            title="ファイルからインポート"
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-import-line text-xs" />
            </div>
            読込
          </button>
          <button
            onClick={() => setShowExamples(!showExamples)}
            className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textPrimary} px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap`}
          >
            {showExamples ? '非表示' : 'サンプル'}
          </button>
        </div>
      </div>

      {showExamples && (
        <div className="glass rounded-xl p-3 animate-float-in">
          <p className={`text-xs ${textDim} mb-2`}>クリックして使用：</p>
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLE_EXPRESSIONS.map((expr) => (
              <button
                key={expr}
                onClick={() => handleExampleClick(expr)}
                className={`glass-btn ripple-btn px-2.5 py-1 text-xs ${isDark ? 'text-cyan-300' : 'text-sky-600'} rounded-lg cursor-pointer whitespace-nowrap hover:${textPrimary} transition-colors`}
              >
                {expr}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {functions.map((fn, idx) => (
          <div key={fn.id} className="group relative animate-float-in">
            <div className={`glass flex items-center gap-2.5 rounded-xl px-3 py-2.5 transition-all duration-200 ${
              fn.error && fn.expression.trim()
                ? 'border-red-500/40'
                : 'hover:border-opacity-30'
            }`}>
              {/* Color dot */}
              <button
                className="w-3.5 h-3.5 flex-shrink-0 rounded-full cursor-pointer ring-1 ring-white/20 hover:ring-white/50 hover:scale-125 transition-all duration-150 ripple-btn"
                style={{ backgroundColor: fn.color }}
                onClick={() => {
                  const nextIdx = (PRESET_COLORS.indexOf(fn.color) + 1) % PRESET_COLORS.length;
                  onUpdate(fn.id, { color: PRESET_COLORS[nextIdx] });
                }}
                title="クリックで色変更"
              />
              <span className={`${textDim} text-xs font-mono flex-shrink-0`}>y =</span>
              <input
                type="text"
                value={fn.expression}
                onChange={(e) => onUpdate(fn.id, { expression: e.target.value })}
                onFocus={() => setActiveExampleFor(fn.id)}
                placeholder={['sin(x)', 'x^2 - 2', '式を入力...'][idx] ?? '式を入力...'}
                className={`flex-1 bg-transparent ${textPrimary} text-sm font-mono ${placeholder} outline-none min-w-0`}
              />
              <button
                onClick={() => onUpdate(fn.id, { visible: !fn.visible })}
                className={`flex-shrink-0 cursor-pointer opacity-60 hover:opacity-100 transition-opacity`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`text-sm ${fn.visible ? `ri-eye-line ${textSecondary}` : `ri-eye-off-line ${textDim}`}`} />
                </div>
              </button>
              {functions.length > 1 && (
                <button
                  onClick={() => onRemove(fn.id)}
                  className="flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line text-sm text-red-400" />
                  </div>
                </button>
              )}
            </div>
            {fn.error && fn.expression.trim() && (
              <p className="text-red-400/80 text-xs mt-1 px-1">{fn.error}</p>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={onAdd}
        className={`glass-btn ripple-btn w-full py-2.5 rounded-xl text-sm ${textMuted} hover:${textPrimary} transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border-dashed`}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-add-line text-sm" />
        </div>
        関数を追加
      </button>
    </div>
  );
}
